import { motion } from 'framer-motion';
import { AlertCircle, RefreshCw } from 'lucide-react';
import * as React from 'react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

/* -------------------------------------------------------------------------- */
/* Skeleton                                                                    */
/* -------------------------------------------------------------------------- */

/**
 * A loading placeholder.
 *
 * Deliberately NOT a generic pulsing grey box. A skeleton should be the *shape*
 * of the content that is coming — same height, same rhythm — so the layout does
 * not jump when the data lands. Layout shift on load is the single most common
 * way an otherwise polished app feels broken.
 */
export function Skeleton({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={cn('bg-muted relative overflow-hidden rounded-md', 'skeleton-shimmer', className)}
      {...props}
    />
  );
}

/** Matches the exact geometry of one queue row, so the table does not reflow. */
export function TableRowSkeleton({ rows = 6 }: { rows?: number }) {
  return (
    <div className="divide-border divide-y">
      {Array.from({ length: rows }).map((_, i) => (
        <div key={i} className="flex items-center gap-4 px-4 py-4">
          <Skeleton className="size-4 rounded" />
          <div className="flex-1 space-y-2">
            {/* Varying widths — uniform bars read as a loading *pattern*, which
                looks synthetic. Real text is ragged. */}
            <Skeleton className="h-4" style={{ width: `${55 + ((i * 13) % 35)}%` }} />
            <Skeleton className="h-3 w-24" />
          </div>
          <Skeleton className="h-6 w-24 rounded-md" />
          <div className="flex -space-x-2">
            <Skeleton className="size-7 rounded-full" />
            <Skeleton className="size-7 rounded-full" />
          </div>
          <Skeleton className="h-4 w-28" />
          <Skeleton className="size-8 rounded-md" />
        </div>
      ))}
    </div>
  );
}

export function StatCardSkeleton() {
  return (
    <div className="bg-card rounded-xl border p-6">
      <div className="flex items-center justify-between">
        <Skeleton className="h-3.5 w-24" />
        <Skeleton className="size-8 rounded-lg" />
      </div>
      <Skeleton className="mt-4 h-8 w-16" />
      <Skeleton className="mt-2 h-3 w-32" />
    </div>
  );
}

export function AccountRowSkeleton() {
  return (
    <div className="flex items-center gap-3 px-3 py-2">
      <Skeleton className="size-8 rounded-full" />
      <div className="flex-1 space-y-1.5">
        <Skeleton className="h-3.5 w-24" />
        <Skeleton className="h-3 w-16" />
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Empty state                                                                 */
/* -------------------------------------------------------------------------- */

interface EmptyStateProps {
  icon: React.ComponentType<{ className?: string }>;
  title: string;
  /**
   * What to do next. An empty state that only says "No posts" wastes the one
   * moment the user is definitely looking for guidance — always say what they
   * can do about it.
   */
  description: string;
  action?: { label: string; onClick: () => void };
  secondaryAction?: { label: string; onClick: () => void };
  className?: string;
}

export function EmptyState({
  icon: Icon,
  title,
  description,
  action,
  secondaryAction,
  className,
}: EmptyStateProps) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
      className={cn('flex flex-col items-center justify-center px-6 py-16 text-center', className)}
    >
      <div className="bg-muted/40 mb-4 flex size-12 items-center justify-center rounded-xl border">
        <Icon className="text-muted-foreground size-5" />
      </div>

      <h3 className="text-sm font-semibold">{title}</h3>
      <p className="text-muted-foreground mt-1.5 max-w-sm text-sm leading-relaxed">{description}</p>

      {(action || secondaryAction) && (
        <div className="mt-6 flex items-center gap-2">
          {action && (
            <Button size="sm" onClick={action.onClick}>
              {action.label}
            </Button>
          )}
          {secondaryAction && (
            <Button size="sm" variant="outline" onClick={secondaryAction.onClick}>
              {secondaryAction.label}
            </Button>
          )}
        </div>
      )}
    </motion.div>
  );
}

/* -------------------------------------------------------------------------- */
/* Error state                                                                 */
/* -------------------------------------------------------------------------- */

/**
 * An inline failure with a way out.
 *
 * Always offers Retry. A dead end tells the user their only option is to reload
 * the whole page — which loses whatever they had typed.
 */
export function ErrorState({
  title = 'Something went wrong',
  message,
  onRetry,
  className,
}: {
  title?: string;
  message: string;
  onRetry?: () => void;
  className?: string;
}) {
  return (
    <div
      className={cn('flex flex-col items-center justify-center px-6 py-16 text-center', className)}
      role="alert"
    >
      <div className="border-destructive/20 bg-destructive/10 mb-4 flex size-12 items-center justify-center rounded-xl border">
        <AlertCircle className="text-destructive size-5" />
      </div>

      <h3 className="text-sm font-semibold">{title}</h3>
      <p className="text-muted-foreground mt-1.5 max-w-sm text-sm leading-relaxed">{message}</p>

      {onRetry && (
        <Button size="sm" variant="outline" className="mt-6" onClick={onRetry}>
          <RefreshCw />
          Try again
        </Button>
      )}
    </div>
  );
}
